/**
 * engineNormalJson_Merge.ts - MergeTemplates implementation for EngineNormalJson
 * Pure ES Module version
 * Port of C# EngineNormalJson_Merge.cs
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { mergeDataJsonIntoHtmlJson, mergeContextIntoDataJson } from '../../base/engine/htmlJsonMergeUtil.js';
import { JsonObject, JsonValue } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { DynamicResultsJson } from '../../model/dynamicResultsJson.js';
import { MergeResult } from '../../../../Assembler/dist/model/mergeResult.js';
import { TransientStore } from '../../../../Assembler/dist/registry/transientStore.js';

import { EngineNormalJson } from './engineNormalJson.js';
import {
    accumulateDynamicDataTimingFromLoader,
    accumulateDynamicViewTimingFromLoader,
    accumulateDynamicActionTimingFromLoader,
    accumulateDynamicClientDataTimingFromLoader
} from './engineNormalJson_Helper.js';
import type { TemplateOperation } from '../../../../Assembler/dist/model/templateOperation.js';
import type { OperationsMetadata } from '../../../../Assembler/dist/model/operationsMetadata.js';

/**
 * Merges templates by processing HtmlJson/DataJson structures directly (no string parsing)
 * Components are resolved on-the-fly using loader (no metadata required)
 */
export function mergeTemplates(this: EngineNormalJson, appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, configJson: JsonObject | null = null, actionMode: boolean = false, clientMode: boolean = false): MergeResult {
    const engine = this;

    Logger.flow(`MergeTemplates: enter appSite=${appSite} appView=${appView || ''} actionMode=${actionMode}`, "NormalJson.MergeLifecycle");
    // AppSite-triggered resolution (loader-owned): an IAppSite handler may redefine the AppSite/AppView
    // before any appfile/template resolution. Unchanged when none applies.
    const appSiteResolved = engine._loader!.applyDynamicAppSiteHandlers(appSite, appView, contextJson ?? new JsonObject(), configJson);
    appSite = appSiteResolved.appSite;
    appView = appSiteResolved.appView;
    // Drain the per-merge navigation trace (ordered IAppSite hops) accumulated at the merge top.
    const navigationTrace = engine._loader!.getAndClearNavigationTrace();
    // Resolve appFile and validate appView via loader
    const appFileResult = engine._loader!.getAppFile(appSite, appView || '');
    const appFile: string = appFileResult.appFile;
    appView = appFileResult.validatedAppView;
    const { mergeContextJson, mergeConfigJson } = engine.prepareProtectedMergeContext(appSite, appView, appFile, contextJson, configJson);
    Logger.flow(`MergeTemplates: appFile=${appFile}`, "NormalJson.MergeLifecycle");

    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
        Logger.debug(`MergeTemplates called: appSite=${appSite}, appFile=${appFile}, appView=${appView || 'null'}`, 'EngineNormalJson');
    }

    // Create fresh DynamicResultsJson per merge and set on loader
    const dynamicResultsJson = engine.initializeDynamicMergeState(appSite, appFile, appView, actionMode);

    // Initialize componentHtml Map
    const componentHtml = new JsonObject();

    try {
        // Create context stack for inheritance resolution (supports multi-parent scenarios)
        const flowStack = [appFile];

        // Set actionMode on loader for DynamicAction handlers
        engine._loader!.setActionMode(actionMode);

        // STEP 1: Get root template's HtmlJson and DataJson
        const startGetTemplate = performance.now();
        let { htmlJson: rootHtmlJson, dataJson: rootDataJson, resolvedTemplateName: rootResolvedName, resolvedAppSite: resolvedRootAppSite } = engine._loader!.getTemplateWithView(appSite, appFile, appFile, appView, flowStack, mergeConfigJson, clientMode);
        engine._getTemplateNanos += EngineNormalJson.msToNanos(performance.now() - startGetTemplate);
        engine._getTemplateCount++;

        // Update flowStack with resolved template name for correct inheritance lookup
        if (rootResolvedName && rootResolvedName !== appFile && flowStack.length > 0) {
            flowStack[flowStack.length - 1] = rootResolvedName;
        }

        if (!rootHtmlJson) {
            Logger.flow(`MergeTemplates: mainTemplate=missing`, "NormalJson.MergeLifecycle");
            Logger.warn(`Root HtmlJson not found: ${appSite}/${appFile}`, 'EngineNormalJson');
            componentHtml.set(appFile, `<!-- Error: Root template not found: ${appSite}/${appFile} -->`);
            const missingResult = new MergeResult(appFile, componentHtml);
            missingResult.resolvedAppSite = appSiteResolved.appSite;
            missingResult.resolvedAppView = appSiteResolved.appView;
            missingResult.navigationTrace = navigationTrace;
            return missingResult;
        }
        Logger.flow(`MergeTemplates: mainTemplate=found`, "NormalJson.MergeLifecycle");

        // STEP 2: Merge DataJson into HtmlJson using utility (follows architecture - engine handles JSON merging)
        // Always merge to apply contextJson values (AppSite, AppView, AppFile) even if template has no DataJson
        const operations: TemplateOperation[] = [];
        // Route KeyReplace/KeyMissing into staging lists so MergeTemplate places them in the
        // canonical phase position (after ArrayIteration, before ConditionalBlock).
        const rootKeyReplaceStage: TemplateOperation[] = [];
        const rootKeyMissingStage: TemplateOperation[] = [];
        const templateKey = `${resolvedRootAppSite}_${appFile}`;
        const dataJsonToMerge = rootDataJson ?? new JsonObject();
        const startJsonMerge = performance.now();
        const mergedRootHtmlJson = mergeDataJsonIntoHtmlJson(rootHtmlJson, dataJsonToMerge, templateKey, appFile, operations, engine._loader!.defaultReplaceHtml, false, mergeContextJson, null, rootKeyReplaceStage, rootKeyMissingStage);
        // Only count when template has actual DataJson, to align with Normal engine count
        if (rootDataJson) {
            engine._jsonMergeNanos += EngineNormalJson.msToNanos(performance.now() - startJsonMerge);
            engine._jsonMergeCount++;
        }

        // STEP 3: Merge recursively using merged HtmlJson structure (JsonKey entries already replaced by loader)
        // Note: Slot/Placeholder timing is captured inside mergeTemplate for each individual operation
        let dataJson: JsonObject = rootDataJson ? new JsonObject(rootDataJson) : new JsonObject();

        // Merge context JSON into dataJson for array iteration.
        // Arrays use smart merge (append/skip) when ArrayNamePrimary found in contextJson.
        if (mergeContextJson && mergeContextJson.size > 0) {
            dataJson = mergeContextIntoDataJson(dataJson, mergeContextJson);
        }
        Logger.flow(`MergeTemplates: mainMerge=enter templateKey=${templateKey}`, "NormalJson.MergeLifecycle");
        let mergedHtml = engine.mergeTemplate(
            mergedRootHtmlJson,
            appSite,
            appFile,
            appView,
            templateKey,
            operations,
            dataJson,
            componentHtml,
            flowStack,
            genComponentHtml,
            mergeContextJson,
            mergeConfigJson,
            0,
            null,
            rootKeyReplaceStage,
            rootKeyMissingStage,
            null,
            clientMode
        );
        Logger.flow(`MergeTemplates: exit components=${componentHtml.size + 1}`, "NormalJson.MergeLifecycle");

        // Collect inheritance operations from loader
        const inheritanceOps = engine._loader!.getAndClearInheritanceOperations();

        // STEP 4: Build operations metadata if needed
        let opsMetadata: OperationsMetadata | null = null;
        if (genOperationJson) {
            opsMetadata = {
                appSite,
                appFile,
                appView: appView || '',
                templateName: templateKey,
                operations,
                hasInheritance: inheritanceOps.length > 0,
                inheritanceOperations: inheritanceOps
            } as OperationsMetadata;
        }

        // Get dynamic data, dynamic view, and dynamic action results from engine-owned DynamicResultsJson
        const { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient } = engine.collectDynamicHandlerResults(dynamicResultsJson);

        // STEP 5: Return tuple with componentHtml Map
        // If merge produced empty output (e.g., empty template), use defaultReplaceHtml
        const finalHtml = (!mergedHtml || mergedHtml.length === 0) ? engine._loader!.defaultReplaceHtml : mergedHtml;
        // Server generation (§2B.4): join the Embedded client-data islands into the FINAL page HTML as a
        // SEPARATE DataHtml — one island per component keyed by component name, sourced from the raw the
        // preparers recorded — inserted before the last </body> (or appended for a fragment), never baked into
        // the reusable component HtmlJson, so the client can reconstruct the clean component and re-attach the
        // island reproducibly.
        componentHtml.set(appFile, CommonUtil.appendClientDataIslands(finalHtml, dynamicResultsJson.dynamicClientDataResults));
        const result = new MergeResult(appFile, componentHtml, opsMetadata, dynamicData, dynamicView, dynamicAction);
        result.dynamicClientData = dynamicClientData;
        result.dynamicClient = dynamicClient;
        result.resolvedAppSite = appSiteResolved.appSite;
        result.resolvedAppView = appSiteResolved.appView;
        result.navigationTrace = navigationTrace;
        return result;
    } catch (ex) {
        Logger.error(`Merge failed: ${(ex as Error).message}`, 'EngineNormalJson');
        Logger.fallback("F-ENG1", `merge exception surfaced: appSite=${appSite} appFile=${appFile} ex=${(ex as Error).message}`);
        throw ex;
    } finally {
        engine._loader!.setActionMode(false);
    }
}

export function prepareProtectedMergeContext(this: EngineNormalJson, appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null): { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null } {
    return {
        mergeContextJson: contextJson ? this.createProtectedMergeContext(appSite, appView, appFile, 'NormalJson', contextJson) : null,
        mergeConfigJson: this.createProtectedMergeConfig(configJson)
    };
}

export function initializeDynamicMergeState(this: EngineNormalJson, appSite: string, appFile: string, appView: string | null, actionMode: boolean): DynamicResultsJson {
    void appSite;
    void appFile;
    void appView;
    void actionMode;
    // Create fresh DynamicResultsJson per merge and set on loader
    const dynamicResultsJson = new DynamicResultsJson();
    this._loader!.setDynamicResults(dynamicResultsJson);
    const transientStore = new TransientStore();
    this._loader!.setTransientStore(transientStore);
    return dynamicResultsJson;
}

export function collectDynamicHandlerResults(this: EngineNormalJson, dynamicResultsJson: DynamicResultsJson): { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject } {
    // Collect dynamic timing from loader and accumulate.
    accumulateDynamicDataTimingFromLoader(this);
    accumulateDynamicViewTimingFromLoader(this);
    accumulateDynamicActionTimingFromLoader(this);
    accumulateDynamicClientDataTimingFromLoader(this);

    // Read dynamic results directly from the per-merge DynamicResultsJson.
    const dynamicData = dynamicResultsJson.dynamicDataResults;
    const dynamicView = dynamicResultsJson.dynamicViewResults;
    const dynamicAction = dynamicResultsJson.dynamicActionResults;
    const dynamicClientData = dynamicResultsJson.dynamicClientDataResults;
    const dynamicClient = dynamicResultsJson.dynamicClientResults;

    return { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient };
}

export function createProtectedMergeContext(this: EngineNormalJson, appSite: string, appView: string | null, appFile: string, engineType: string, contextJson: JsonObject): JsonObject {
    // Build a FRESH immutable merge context. The identity keys are written first onto the new (always
    // mutable) object, so the write can never be silently dropped by a read-only source — that silent
    // drop is the leak that let a stale/blank EngineType survive in some paths/langs. The caller's keys
    // are then copied in only where they don't collide, so the caller can never override
    // AppSite/AppView/AppFile/EngineType. The passed contextJson is never mutated (its values are
    // cloned, nothing is stamped onto it, it is never locked), so the same instance flows unchanged into
    // every engine and serializes plainly into the api-json (no leaked EngineType to diverge across
    // engines or config sources).
    const mergeContextJson = new JsonObject();
    mergeContextJson.set('AppSite', JsonValue.string(appSite));
    mergeContextJson.set('AppView', JsonValue.string(appView || ''));
    mergeContextJson.set('AppFile', JsonValue.string(appFile));
    mergeContextJson.set('EngineType', JsonValue.string(engineType));
    for (const [key, value] of contextJson.entries()) {
        if (!mergeContextJson.containsKey(key)) {
            mergeContextJson.set(key, value.deepClone());
        }
    }
    mergeContextJson.isReadOnly = true;
    return mergeContextJson;
}

export function createProtectedMergeConfig(this: EngineNormalJson, configJson: JsonObject | null): JsonObject | null {
    if (configJson) {
        configJson.isReadOnly = true;
    }
    return configJson;
}
